import { createContext, useContext, useEffect, useState } from 'react'
import type { TelegramWebApp } from '@/types/telegram'

interface TelegramContextType {
  webApp: TelegramWebApp | null
  isReady: boolean
}

const TelegramContext = createContext<TelegramContextType>({
  webApp: null,
  isReady: false,
})

export function useTelegramContext() {
  const context = useContext(TelegramContext)
  if (!context) {
    throw new Error('useTelegramContext must be used within TelegramProvider')
  }
  return context
}

interface TelegramProviderProps {
  children: React.ReactNode
}

export function TelegramProvider({ children }: TelegramProviderProps) {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [isReady, setIsReady] = useState(false)

  // 深色模式：Telegram 內跟隨 colorScheme（含 themeChanged 事件），
  // 一般瀏覽器跟隨系統 prefers-color-scheme
  useEffect(() => {
    const applyTheme = (dark: boolean) => {
      document.documentElement.classList.toggle('dark', dark)
    }

    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
    if (tg?.colorScheme) {
      applyTheme(tg.colorScheme === 'dark')
      const onThemeChanged = () => applyTheme(tg.colorScheme === 'dark')
      tg.onEvent?.('themeChanged', onThemeChanged)
      return () => tg.offEvent?.('themeChanged', onThemeChanged)
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    applyTheme(media.matches)
    const onChange = (e: MediaQueryListEvent) => applyTheme(e.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    const initTelegram = () => {
      if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp
        
        console.log('🚀 [TelegramProvider] 初始化 Telegram Web App')
        console.log('📱 [TelegramProvider] User data:', tg.initDataUnsafe.user)
        console.log('📊 [TelegramProvider] Init data:', tg.initData)
        console.log('🌐 [TelegramProvider] Platform:', tg.platform)
        
        // 初始化 Telegram Web App
        tg.ready()
        tg.expand()
        
        // header 顏色跟隨主題（Geist：淺色 #FAFAFA / 深色 #0A0A0A）
        if (tg.setHeaderColor) {
          tg.setHeaderColor(tg.colorScheme === 'dark' ? '#0A0A0A' : '#FAFAFA')
        }
        
        setWebApp(tg)
        setIsReady(true)
        
        console.log('✅ [TelegramProvider] Telegram Web App 初始化完成')
      }
    }

    // Check if Telegram script is already loaded
    if (typeof window !== 'undefined') {
      console.log('🔍 [TelegramProvider] 檢查 Telegram Web App 環境')
      
      if (window.Telegram?.WebApp) {
        console.log('✅ [TelegramProvider] Telegram Web App 已可用')
        initTelegram()
      } else {
        console.log('⏳ [TelegramProvider] 等待 Telegram Web App 載入...')
        // Wait for script to load — 最多等 5 秒；
        // 非 Telegram 環境下不再無限輪詢（原本每 100ms 永遠跑下去）
        let attempts = 0
        const maxAttempts = 50
        const checkTelegram = () => {
          if (window.Telegram?.WebApp) {
            console.log('✅ [TelegramProvider] Telegram Web App 載入完成')
            initTelegram()
          } else if (attempts < maxAttempts) {
            attempts += 1
            setTimeout(checkTelegram, 100)
          } else {
            console.log('ℹ️ [TelegramProvider] 非 Telegram 環境，停止等待')
          }
        }
        checkTelegram()
      }
    }
  }, [])

  return (
    <TelegramContext.Provider value={{ webApp, isReady }}>
      {children}
    </TelegramContext.Provider>
  )
}